import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  HStack,
  Icon,
  Input,
  List,
  ListItem,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { useCallback, useRef, useState } from 'react';
import { FaCloudUploadAlt, FaFile, FaTrash } from 'react-icons/fa';
import { validateCsvSchema } from '../helpers/csvValidation';
import { CsvRow, CsvValidationError } from '../types/nfse.types';

interface CsvUploaderProps {
  onValidated: (data: CsvRow[], errors: CsvValidationError[]) => void;
  onClear: () => void;
  errors: CsvValidationError[];
}

const CsvUploader: React.FC<CsvUploaderProps> = ({
  onValidated,
  onClear,
  errors,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const borderColor = useColorModeValue('gray.300', 'gray.600');
  const bgColor = useColorModeValue('gray.50', 'gray.700');
  const dragBgColor = useColorModeValue('yooga.50', 'yooga.900');

  const processFile = useCallback(
    async (file: File) => {
      if (!file.name.toLowerCase().endsWith('.csv')) {
        onValidated(
          [],
          [
            {
              row: 0,
              field: 'file',
              message: 'O arquivo deve ser um CSV',
            },
          ],
        );
        return;
      }

      setIsProcessing(true);
      setFileName(file.name);

      try {
        const result = await validateCsvSchema(file);
        onValidated(result.data, result.errors);
      } catch (error) {
        onValidated(
          [],
          [
            {
              row: 0,
              field: 'file',
              message:
                error instanceof Error
                  ? error.message
                  : 'Erro ao processar arquivo',
            },
          ],
        );
      } finally {
        setIsProcessing(false);
      }
    },
    [onValidated],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      const file = e.dataTransfer.files[0];
      if (file) {
        processFile(file);
      }
    },
    [processFile],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        processFile(file);
      }
    },
    [processFile],
  );

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleClear = () => {
    setFileName(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    onClear();
  };

  return (
    <Stack gap="1rem">
      <Input
        ref={inputRef}
        type="file"
        accept=".csv"
        onChange={handleFileChange}
        display="none"
      />

      {!fileName ? (
        <Box
          border="2px dashed"
          borderColor={isDragging ? 'yooga.500' : borderColor}
          borderRadius="lg"
          bg={isDragging ? dragBgColor : bgColor}
          p="3rem"
          textAlign="center"
          cursor="pointer"
          transition="all 0.2s"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
          _hover={{
            borderColor: 'yooga.400',
            bg: dragBgColor,
          }}
        >
          <Stack align="center" gap="1rem">
            <Icon
              as={FaCloudUploadAlt}
              boxSize="3rem"
              color={isDragging ? 'yooga.500' : 'gray.400'}
            />
            <Text color="gray.600" fontWeight="medium">
              {isDragging
                ? 'Solte o arquivo aqui'
                : 'Arraste um arquivo CSV ou clique para selecionar'}
            </Text>
            <Text color="gray.400" fontSize="sm">
              O arquivo deve conter as colunas: documento, valor
            </Text>
          </Stack>
        </Box>
      ) : (
        <Box
          border="1px solid"
          borderColor={borderColor}
          borderRadius="lg"
          bg={bgColor}
          p="1rem"
        >
          <HStack justify="space-between">
            <HStack>
              <Icon as={FaFile} color="yooga.500" />
              <Text fontWeight="medium">{fileName}</Text>
              {isProcessing && <Text color="gray.500">Processando...</Text>}
            </HStack>
            <Button
              size="sm"
              variant="ghost"
              colorScheme="red"
              leftIcon={<FaTrash />}
              onClick={handleClear}
            >
              Remover
            </Button>
          </HStack>
        </Box>
      )}

      {errors.length > 0 && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <Stack gap="0.5rem" flex="1">
            <AlertDescription fontWeight="medium">
              Erros encontrados no arquivo:
            </AlertDescription>
            <List spacing={1} fontSize="sm">
              {errors.slice(0, 10).map((error, idx) => (
                <ListItem key={idx}>
                  {error.row > 0 ? `Linha ${error.row}: ` : ''}
                  {error.message}
                </ListItem>
              ))}
              {errors.length > 10 && (
                <ListItem color="gray.500">
                  ... e mais {errors.length - 10} erros
                </ListItem>
              )}
            </List>
          </Stack>
        </Alert>
      )}
    </Stack>
  );
};

export default CsvUploader;
